import React, { useState, useEffect } from 'react';
import { 
  FaRocket,
  FaCode,
  FaServer,
  FaUser,
  FaGraduationCap,
  FaBriefcase,
  FaTrophy,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaChevronRight,
  FaCaretDown
} from 'react-icons/fa';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      const sections = ['home', 'profile', 'education', 'skills', 'experience', 'projects', 'achievements', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { 
      name: 'Home', 
      path: '#home', 
      icon: <FaRocket />, 
      color: 'text-blue-500', 
      bgColor: 'bg-blue-500/10',
      gradient: 'from-blue-500 to-cyan-500'
    },
    { 
      name: 'Profile', 
      path: '#profile', 
      icon: <FaUser />, 
      color: 'text-purple-500', 
      bgColor: 'bg-purple-500/10',
      gradient: 'from-purple-500 to-pink-500'
    },
    { 
      name: 'Education', 
      path: '#education', 
      icon: <FaGraduationCap />, 
      color: 'text-emerald-500', 
      bgColor: 'bg-emerald-500/10',
      gradient: 'from-emerald-500 to-green-500'
    },
    { 
      name: 'Skills', 
      path: '#skills', 
      icon: <FaCode />, 
      color: 'text-orange-500', 
      bgColor: 'bg-orange-500/10',
      gradient: 'from-orange-500 to-amber-500'
    },
    { 
      name: 'Experience', 
      path: '#experience', 
      icon: <FaBriefcase />, 
      color: 'text-indigo-500', 
      bgColor: 'bg-indigo-500/10',
      gradient: 'from-indigo-500 to-blue-500'
    },
    { 
      name: 'Projects', 
      path: '#projects', 
      icon: <FaServer />, 
      color: 'text-red-500', 
      bgColor: 'bg-red-500/10',
      gradient: 'from-red-500 to-orange-500'
    },
    { 
      name: 'Achievements', 
      path: '#achievements', 
      icon: <FaTrophy />, 
      color: 'text-yellow-500', 
      bgColor: 'bg-yellow-500/10',
      gradient: 'from-yellow-500 to-amber-500'
    },
    { 
      name: 'Contact', 
      path: '#contact', 
      icon: <FaEnvelope />, 
      color: 'text-cyan-500', 
      bgColor: 'bg-cyan-500/10',
      gradient: 'from-cyan-500 to-teal-500'
    },
  ];

  return (
    <>
      {/* Professional & Attractive Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-gray-950/95 backdrop-blur-xl shadow-2xl shadow-black/30 py-4 border-b border-gray-800/50' 
          : 'bg-gray-950/90 backdrop-blur-lg shadow-lg shadow-black/20 py-5'
      }`}>
        
        {/* Animated Gradient Border */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600"></div>
        
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25px 25px, rgba(120, 119, 198, 0.3) 2%, transparent 0%), 
                            radial-gradient(circle at 75px 75px, rgba(120, 119, 198, 0.3) 2%, transparent 0%)`,
            backgroundSize: '100px 100px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between">
            {/* Enhanced Logo and Brand Section */}
            <a href="#home" className="group flex items-center space-x-4">
              {/* Animated Logo Container */}
              <div className="relative">
                {/* Outer Glow Effect */}
                <div className="absolute -inset-3 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Main Logo */}
                <div className="relative w-12 h-12 bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-700/50 rounded-xl flex items-center justify-center shadow-2xl overflow-hidden group-hover:border-blue-500/30 transition-all duration-500">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent relative z-10">
                    TK
                  </span>
                  
                  {/* Animated Corner Dots */}
                  <div className="absolute top-1 right-1 w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
                  <div className="absolute bottom-1 left-1 w-1.5 h-1.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                </div>
              </div>
              
              {/* Name and Title with Enhanced Typography */}
              <div className="transform group-hover:translate-x-1 transition-transform duration-300">
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-100 via-gray-200 to-gray-300 bg-clip-text text-transparent">
                  Tehseen Nayeem Khan
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-ping opacity-30"></div>
                    </div>
                    <span className="text-sm text-gray-400 font-medium bg-gray-900/50 px-3 py-1 rounded-full border border-gray-700/50 backdrop-blur-sm">
                      DevOps Engineer
                    </span>
                  </div>
                  
                  {/* Professional Badge */}
                  <div className="hidden md:flex items-center space-x-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700/50 backdrop-blur-sm">
                    <FaCaretDown className="h-3 w-3 text-blue-400 animate-bounce" />
                    <span className="text-xs font-semibold text-blue-400">Professional Portfolio</span>
                  </div>
                </div>
              </div>
            </a>

            {/* Enhanced Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2">
              {navLinks.map((link) => {
                const isActive = activeSection === link.path.substring(1);
                return (
                  <a
                    key={link.name}
                    href={link.path}
                    className={`group relative px-4 py-2.5 rounded-xl transition-all duration-300 overflow-hidden ${
                      isActive 
                        ? `bg-gradient-to-r ${link.gradient} text-white shadow-lg transform scale-105` 
                        : 'text-gray-400 hover:text-gray-200 bg-gray-900/30 hover:bg-gray-800/50 border border-transparent hover:border-gray-700/50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {/* Hover Background Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${link.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    <div className="flex items-center space-x-2 relative z-10">
                      <div className={`transform transition-all duration-300 ${
                        isActive ? 'scale-110 rotate-12' : 'group-hover:scale-110 group-hover:rotate-12'
                      }`}>
                        {link.icon}
                      </div>
                      <span className="font-medium text-sm whitespace-nowrap">{link.name}</span>
                    </div>
                    
                    {/* Active Indicator */}
                    {isActive && (
                      <>
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-white/50 rounded-full blur-sm"></div>
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-white/20 to-transparent rounded-xl blur opacity-30"></div>
                      </>
                    )}
                    
                    {/* Hover Indicator */}
                    {!isActive && (
                      <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r ${link.gradient} rounded-full transition-all duration-500 group-hover:w-8`}></div>
                    )}
                  </a>
                );
              })}
            </nav>

            {/* Enhanced Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                className="group relative p-3 rounded-xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-700/50 hover:border-blue-500/30 shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {/* Button Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-cyan-500/0 opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300"></div>
                
                <div className="relative">
                  {isMenuOpen ? (
                    <FaTimes className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  ) : (
                    <FaBars className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  )}
                </div>
                
                {/* Pulsing Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </button>
            </div>
          </div>

          {/* Enhanced Mobile Navigation */}
          <div className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMenuOpen ? 'max-h-[650px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-cyan-500/20"></div>
              </div>
              
              <div className="relative z-10">
                {/* Navigation Links Grid */}
                <div className="grid grid-cols-2 gap-3 p-4">
                  {navLinks.map((link) => {
                    const isActive = activeSection === link.path.substring(1);
                    return (
                      <a
                        key={link.name}
                        href={link.path}
                        className={`group relative p-4 rounded-xl transition-all duration-300 overflow-hidden ${
                          isActive
                            ? `bg-gradient-to-r ${link.gradient} text-white shadow-lg transform scale-105` 
                            : 'bg-gray-900/50 hover:bg-gray-800/70 border border-gray-700/50 hover:border-gray-600/50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {/* Hover Background Effect */}
                        <div className={`absolute inset-0 bg-gradient-to-r ${link.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                        
                        <div className="relative z-10 flex flex-col items-center">
                          <div className={`text-2xl mb-2 transform transition-all duration-300 ${
                            isActive ? 'scale-110 rotate-12 animate-bounce' : 'group-hover:scale-110 group-hover:rotate-12'
                          }`}>
                            {link.icon}
                          </div>
                          <span className={`font-semibold text-sm ${
                            isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'
                          }`}>
                            {link.name}
                          </span>
                        </div>
                        
                        {/* Active Checkmark */}
                        {isActive && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <FaChevronRight className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
                
                {/* Mobile Contact Info */}
                <div className="p-4 border-t border-gray-700/50">
                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700/50">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                      <span className="text-xs font-semibold text-emerald-400">Available for Opportunities</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Responsive Spacer for Fixed Header */}
      <div className="h-16 sm:h-20 md:h-24"></div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.5);
          }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Header;